import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ReactFlow, ReactFlowProvider, Background, Controls, MiniMap,
  applyNodeChanges, applyEdgeChanges, addEdge, useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { ArrowLeft, Save, Sparkles, Play, ToggleLeft, ToggleRight, Loader2, History, BarChart3, Wrench, CheckCircle2, AlertTriangle, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import SubAccountLayout, { useSubAccount } from '../../components/SubAccountLayout';
import CallFlowNode from '../../components/callflow/CallFlowNode';
import NodePalette from '../../components/callflow/NodePalette';
import NodeConfigDrawer from '../../components/callflow/NodeConfigDrawer';
import LiveTestModal from '../../components/callflow/LiveTestModal';
import ExecutionHistoryDrawer from '../../components/callflow/ExecutionHistoryDrawer';
import ReplayPlayer from '../../components/callflow/ReplayPlayer';
import { decorateEdges, getUnwiredHandleIds } from '../../components/callflow/edgeDecorator';
import { tidyLayout, legacyToNodes } from '../../components/callflow/layoutUtils';
import { NODE_DEFAULTS } from '../../components/callflow/nodeTypes';

/* Shared CSS overrides so React Flow blends with the Voxinity dark theme.
   Injected once (outside the component) via a stylesheet tag. */
// Shared canvas styling — injects once for the editor AND the public replay page
import '../../components/callflow/flowStyles';

const nodeTypes = {
  start: CallFlowNode, menu: CallFlowNode, time_router: CallFlowNode,
  tag_filter: CallFlowNode, contact_filter: CallFlowNode,
  ai_agent: CallFlowNode, transfer: CallFlowNode, hangup: CallFlowNode,
};

function CanvasInner() {
  const { subAccountId, flowId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  useSubAccount();

  const [flow, setFlow] = useState(null);
  const [agents, setAgents] = useState([]);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState(null);
  const { isDarkMode } = useTheme();
  const theme = isDarkMode ? 'dark' : 'light';

  // Live-test state
  const [liveSession, setLiveSession] = useState(null); // {session_id, expires_at, phone_number, ...}
  const [wsStatus, setWsStatus] = useState('idle'); // idle|connecting|live|ended
  const [liveSteps, setLiveSteps] = useState([]); // visible in modal log
  const [pulsedNodeIds, setPulsedNodeIds] = useState({}); // nodeId -> {status, ts}
  const [historyOpen, setHistoryOpen] = useState(false);
  // Replay state — when set, the timeline scrubber appears at the bottom
  // and pulsedNodeIds is driven by stepIndex instead of live WebSocket.
  const [replay, setReplay] = useState(null); // {exec, stepIndex, playing, speed}
  // Hover focus — when set, edges NOT touching this node fade so the
  // user can follow a single branch end-to-end without other wires
  // visually overlapping.
  const [hoveredNodeId, setHoveredNodeId] = useState(null);
  // Live-traffic overlay — when enabled, fetch per-edge call counts
  // from the last 7 days and append them to the wire labels.
  const [trafficEnabled, setTrafficEnabled] = useState(false);
  const [trafficData, setTrafficData] = useState(null);
  const [trafficLoading, setTrafficLoading] = useState(false);
  const wsRef = useRef(null);

  const flowRef = useRef(null);
  const rf = useReactFlow();

  /* ─── Load flow + agents ─────────────────────────────────────────────── */
  useEffect(() => {
    (async () => {
      try {
        const [fRes, aRes, pRes] = await Promise.all([
          api.get(`/api/sub-accounts/${subAccountId}/call-routing/${flowId}`),
          api.get(`/api/sub-accounts/${subAccountId}/agents`),
          api.get(`/api/sub-accounts/${subAccountId}/phone-numbers`),
        ]);
        const f = fRes.data;
        setFlow(f);
        setAgents(aRes.data?.agents || aRes.data || []);
        setPhoneNumbers(Array.isArray(pRes.data) ? pRes.data : (pRes.data?.phone_numbers || []));

        // Auto-migrate: if the flow has no nodes, generate them from the
        // legacy schedule shape so the user has something to work with.
        if (!f.nodes || f.nodes.length === 0) {
          const seed = legacyToNodes(f);
          setNodes(seed.nodes);
          setEdges(seed.edges);
        } else {
          setNodes(f.nodes);
          setEdges(f.edges || []);
        }
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Failed to load flow');
      }
    })();
  }, [subAccountId, flowId]);

  /* ─── Canvas event handlers ──────────────────────────────────────────── */
  const onNodesChange = useCallback((changes) => setNodes((ns) => applyNodeChanges(changes, ns)), []);
  const onEdgesChange = useCallback((changes) => setEdges((es) => applyEdgeChanges(changes, es)), []);
  const onConnect = useCallback((conn) =>
    setEdges((es) => addEdge({ ...conn, type: 'smoothstep', animated: true, id: `e_${conn.source}_${conn.target}_${conn.sourceHandle || 'out'}` }, es)),
  []);

  const onDragOver = useCallback((e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }, []);
  const onDrop = useCallback((e) => {
    e.preventDefault();
    const type = e.dataTransfer.getData('application/voxinity-flow-node');
    if (!type) return;
    const bounds = flowRef.current.getBoundingClientRect();
    const position = rf.screenToFlowPosition({ x: e.clientX - bounds.left, y: e.clientY - bounds.top });
    const id = `n_${type}_${Date.now()}`;
    const newNode = { id, type, position, data: { ...(NODE_DEFAULTS[type] || {}) } };
    setNodes((ns) => [...ns, newNode]);
  }, [rf]);

  const onNodeClick = useCallback((_, n) => setSelected(n), []);
  const onPaneClick = useCallback(() => setSelected(null), []);

  const updateNode = useCallback((updated) => {
    setNodes((ns) => ns.map((n) => (n.id === updated.id ? updated : n)));
    setSelected(null);
    // Start nodes carry no meaningful per-node data — the only thing
    // worth saving is the bound phone number, which has its own toast
    // via updateFlowPhoneNumber. Skip the generic toast to avoid duplicates.
    if (updated.type !== 'start') {
      toast.success('Node saved');
    }
  }, []);

  // Update the bound inbound number on the parent flow (called when the
  // user edits the Start node's number picker — Start has no per-node
  // data; the number lives on the flow doc).
  const updateFlowPhoneNumber = useCallback(async (newNumber) => {
    if (!newNumber || newNumber === flow?.phone_number) return;
    try {
      const r = await api.put(`/api/sub-accounts/${subAccountId}/call-routing/${flowId}`, { phone_number: newNumber });
      const updated = r.data || {};
      setFlow(updated);
      // Refresh phone-numbers so dropdown labels reflect the new binding
      try {
        const pRes = await api.get(`/api/sub-accounts/${subAccountId}/phone-numbers`);
        setPhoneNumbers(Array.isArray(pRes.data) ? pRes.data : (pRes.data?.phone_numbers || []));
      } catch (_e) { /* non-fatal */ }
      const rebind = updated._rebind;
      if (rebind?.conflict_with?.agent_name) {
        toast.success(`Detached from agent "${rebind.conflict_with.agent_name}" and bound to ${newNumber}`);
      } else if (rebind?.provider === 'telnyx' && !rebind.ok) {
        toast.warning(`Bound to ${newNumber}, but Telnyx routing webhook isn't built yet — calls still hit the agent webhook`);
      } else {
        toast.success(`Bound number updated to ${newNumber}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not update number');
    }
  }, [api, subAccountId, flowId, flow?.phone_number]);

  const deleteNode = useCallback(() => {
    if (!selected) return;
    setNodes((ns) => ns.filter((n) => n.id !== selected.id));
    setEdges((es) => es.filter((e) => e.source !== selected.id && e.target !== selected.id));
    setSelected(null);
    toast.success('Node deleted');
  }, [selected]);

  // Standalone delete used by the on-node hover × button. Cannot reuse
  // `deleteNode` above because that depends on `selected`.
  const deleteNodeById = useCallback((id) => {
    setNodes((ns) => ns.filter((n) => n.id !== id));
    setEdges((es) => es.filter((e) => e.source !== id && e.target !== id));
    setSelected((sel) => (sel && sel.id === id ? null : sel));
    toast.success('Node deleted');
  }, []);

  /* ─── Toolbar actions ────────────────────────────────────────────────── */
  const tidy = useCallback(() => {
    setNodes((ns) => tidyLayout(ns, edges));
    setTimeout(() => rf.fitView({ padding: 0.2, duration: 400 }), 50);
    toast.success('Tidied');
  }, [edges, rf]);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const viewport = rf.getViewport();
      await api.put(`/api/sub-accounts/${subAccountId}/call-routing/${flowId}`, {
        nodes, edges, canvas_viewport: viewport,
      });
      toast.success('Flow saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [nodes, edges, subAccountId, flowId, rf]);

  const toggleEnabled = useCallback(async () => {
    if (!flow) return;
    try {
      const r = await api.put(`/api/sub-accounts/${subAccountId}/call-routing/${flowId}`, { enabled: !flow.enabled });
      setFlow(r.data);
      toast.success(r.data.enabled ? 'Flow enabled' : 'Flow disabled');
    } catch {
      toast.error('Toggle failed');
    }
  }, [flow, subAccountId, flowId]);

  const runSimulation = useCallback(async () => {
    setRunning(true);
    setRunResult(null);
    setPulsedNodeIds({});
    try {
      // First save so the simulator sees the latest graph
      await api.put(`/api/sub-accounts/${subAccountId}/call-routing/${flowId}`, { nodes, edges });
      const r = await api.post(`/api/sub-accounts/${subAccountId}/call-routing/${flowId}/simulate`, {});
      setRunResult(r.data);

      // n8n-style: animate each step on the canvas as the simulation
      // walks the graph. Don't rely on the WS hub — drive pulses
      // directly from the returned steps array so users see the flow
      // light up end-to-end every time they click Test.
      const steps = (r.data?.steps || []).filter((s) => s.node_id);
      if (steps.length === 0) return;
      const STEP_MS = 700;
      const HOLD_MS = 2400;
      steps.forEach((s, i) => {
        setTimeout(() => {
          setPulsedNodeIds((prev) => ({ ...prev, [s.node_id]: { status: s.status } }));
        }, i * STEP_MS);
        setTimeout(() => {
          setPulsedNodeIds((prev) => { const n = { ...prev }; delete n[s.node_id]; return n; });
        }, i * STEP_MS + HOLD_MS);
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Simulation failed');
    } finally {
      setRunning(false);
    }
  }, [subAccountId, flowId, nodes, edges, api]);

  /* ─── Verify & Repair ─────────────────────────────────────────────────
     Fetches Twilio's actual current voice_url for the bound number and
     reports whether it matches our expected webhook. Surfaces the silent
     bind failure that causes "inbound calls just keep ringing". */
  const [verifyState, setVerifyState] = useState(null); // {loading, data, diagnostic, error}
  const verifyWebhook = useCallback(async () => {
    // Run BOTH the URL check and the deep diagnostic in parallel so the
    // user sees the full picture in one click — webhook config,
    // simulated chain, recent Twilio calls AND per-call error codes.
    setVerifyState({ loading: true });
    const [vRes, dRes] = await Promise.allSettled([
      api.get(`/api/sub-accounts/${subAccountId}/call-routing/${flowId}/verify-webhook`),
      api.post(`/api/sub-accounts/${subAccountId}/call-routing/${flowId}/diagnose`),
    ]);
    setVerifyState({
      loading: false,
      data: vRes.status === 'fulfilled' ? vRes.value.data : null,
      diagnostic: dRes.status === 'fulfilled' ? dRes.value.data : null,
      error:
        vRes.status === 'rejected'
          ? vRes.reason?.response?.data?.detail || 'Verification failed'
          : dRes.status === 'rejected'
          ? dRes.reason?.response?.data?.detail || 'Diagnostic failed'
          : null,
    });
  }, [subAccountId, flowId, api]);
  const repairWebhook = useCallback(async () => {
    setVerifyState((s) => ({ ...(s || {}), loading: true }));
    try {
      const r = await api.post(`/api/sub-accounts/${subAccountId}/call-routing/${flowId}/repair-webhook`);
      // Preserve the existing diagnostic so the user keeps seeing the
      // full picture after the Repair button click.
      setVerifyState((s) => ({ ...(s || {}), loading: false, data: r.data }));
      if (r.data?.matches) toast.success('Webhook repaired — inbound calls will now reach this flow');
      else toast.error(r.data?.message || 'Repair attempted — see details');
    } catch (err) {
      setVerifyState((s) => ({ ...(s || {}), loading: false, error: err?.response?.data?.detail || 'Repair failed' }));
    }
  }, [subAccountId, flowId, api]);
  const diagnoseFlow = useCallback(async () => {
    setVerifyState((s) => ({ ...(s || {}), loading: true, mode: 'diagnose' }));
    try {
      const r = await api.post(`/api/sub-accounts/${subAccountId}/call-routing/${flowId}/diagnose`);
      // Critical: PRESERVE the verify result (URL match, TwiML App SID,
      // recent Twilio errors). Previously this clobbered it.
      setVerifyState((s) => ({ ...(s || {}), loading: false, mode: 'diagnose', diagnostic: r.data }));
    } catch (err) {
      setVerifyState((s) => ({ ...(s || {}), loading: false, mode: 'diagnose', error: err?.response?.data?.detail || 'Diagnostic failed' }));
    }
  }, [subAccountId, flowId, api]);

  /* ─── Live Test mode ─────────────────────────────────────────────────
     Opens a per-user session on the backend, then subscribes via WS so
     real inbound calls into this flow pulse the canvas in real time. */
  const startLiveTest = useCallback(async () => {
    try {
      // Save first so the running flow is what we'll observe.
      await api.put(`/api/sub-accounts/${subAccountId}/call-routing/${flowId}`, { nodes, edges });
      const r = await api.post(`/api/sub-accounts/${subAccountId}/call-routing/${flowId}/live-test/start`, {});
      const session = r.data;
      setLiveSession(session);
      setLiveSteps([]);
      setPulsedNodeIds({});
      setWsStatus('connecting');

      const token = localStorage.getItem('token');
      const apiBase = process.env.REACT_APP_BACKEND_URL || '';
      const wsUrl = `${apiBase.replace(/^http/, 'ws')}/api/sub-accounts/${subAccountId}/call-routing/${flowId}/live-test/ws?token=${encodeURIComponent(token)}&session_id=${session.session_id}`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onopen = () => setWsStatus('live');
      ws.onclose = () => setWsStatus('ended');
      ws.onerror = () => setWsStatus('ended');
      ws.onmessage = (msg) => {
        try {
          const ev = JSON.parse(msg.data);
          if (ev.type === 'step' && ev.step) {
            setLiveSteps((prev) => [...prev, ev.step]);
            if (ev.step.node_id) {
              setPulsedNodeIds((prev) => ({ ...prev, [ev.step.node_id]: { status: ev.step.status, ts: Date.now() } }));
              // Fade the highlight after 4s so the canvas doesn't stay stuck-on
              setTimeout(() => {
                setPulsedNodeIds((prev) => {
                  const next = { ...prev };
                  delete next[ev.step.node_id];
                  return next;
                });
              }, 4000);
            }
          } else if (ev.type === 'finalize') {
            toast.success('Call execution captured');
          }
        } catch (_e) { /* ignore parse errors */ }
      };
      // Heartbeat ping every 25s to keep proxies happy
      const pingId = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send('ping');
      }, 25000);
      ws.addEventListener('close', () => clearInterval(pingId));

      toast.success('Live Test active — call the number to test');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not start live test');
    }
  }, [api, subAccountId, flowId, nodes, edges]);

  const stopLiveTest = useCallback(async () => {
    const session = liveSession;
    if (!session) return;
    try {
      if (wsRef.current) { try { wsRef.current.close(); } catch (_e) { /* noop */ } wsRef.current = null; }
      await api.post(`/api/sub-accounts/${subAccountId}/call-routing/${flowId}/live-test/stop?session_id=${session.session_id}`, {});
    } catch { /* ignore */ }
    setLiveSession(null);
    setWsStatus('idle');
    setPulsedNodeIds({});
  }, [api, subAccountId, flowId, liveSession]);

  // Auto-stop when the session timer expires
  useEffect(() => {
    if (!liveSession?.expires_at) return;
    const ms = new Date(liveSession.expires_at).getTime() - Date.now();
    if (ms <= 0) { stopLiveTest(); return; }
    const id = setTimeout(stopLiveTest, ms);
    return () => clearTimeout(id);
  }, [liveSession, stopLiveTest]);

  // Replay a historical execution — opens the timeline scrubber player.
  // Stepping through the player drives `pulsedNodeIds` via the useMemo
  // below, so the canvas lights up node-by-node as the user scrubs.
  const replayExecution = useCallback((execution) => {
    setHistoryOpen(false);
    setPulsedNodeIds({});
    setReplay({ exec: execution, stepIndex: 0, playing: true, speed: 1 });
  }, []);

  const replaySteps = useMemo(
    () => (replay?.exec?.steps || []).filter((s) => s.node_id),
    [replay]
  );

  // Derive pulsedNodeIds from replay state — current step is bright, prior
  // steps fade to "visited". When not in replay mode this is a no-op and
  // live-WebSocket pulses from startLiveTest drive pulsedNodeIds directly.
  const replayPulsed = useMemo(() => {
    if (!replay) return null;
    const out = {};
    replaySteps.forEach((s, i) => {
      if (i <= replay.stepIndex) {
        out[s.node_id] = {
          status: i === replay.stepIndex ? s.status : 'visited',
        };
      }
    });
    return out;
  }, [replay, replaySteps]);

  const replayTogglePlay = useCallback(() => {
    setReplay((r) => {
      if (!r) return r;
      // Tapping play while at the end → restart from step 0
      const atEnd = r.stepIndex >= replaySteps.length - 1;
      if (atEnd && !r.playing) return { ...r, stepIndex: 0, playing: true };
      return { ...r, playing: !r.playing };
    });
  }, [replaySteps.length]);

  const replayStepForward = useCallback(() => {
    setReplay((r) => {
      if (!r) return r;
      const next = Math.min(r.stepIndex + 1, Math.max(0, replaySteps.length - 1));
      const atEnd = next >= replaySteps.length - 1;
      return { ...r, stepIndex: next, playing: r.playing && !atEnd };
    });
  }, [replaySteps.length]);

  const replayStepBack = useCallback(() => {
    setReplay((r) => (r ? { ...r, stepIndex: Math.max(0, r.stepIndex - 1), playing: false } : r));
  }, []);

  const replaySeek = useCallback((idx) => {
    setReplay((r) => (r ? { ...r, stepIndex: idx, playing: false } : r));
  }, []);

  const replaySpeed = useCallback((s) => {
    setReplay((r) => (r ? { ...r, speed: s } : r));
  }, []);

  const closeReplay = useCallback(() => {
    setReplay(null);
    setPulsedNodeIds({});
  }, []);

  // Merge live-pulse status into nodes so CallFlowNode can highlight them.
  // Replay mode overrides live pulses (replayPulsed is null otherwise).
  // Also inject a per-node delete callback that's bound here so children
  // never need to know about parent state directly. Start nodes additionally
  // receive the flow's phone number so they render the inbound number.
  // Finally, attach the set of unwired output handles so the node can
  // render its dead-branch warning chips.
  const decoratedNodes = useMemo(() => {
    const activePulses = replayPulsed || pulsedNodeIds;
    return nodes.map((n) => {
      const data = { ...(n.data || {}), _onDelete: deleteNodeById };
      if (n.type === 'start') data._phoneNumber = flow?.phone_number || '';
      const p = activePulses[n.id];
      if (p) data._pulse = p.status;
      data._unwiredHandles = getUnwiredHandleIds(n, edges);
      return { ...n, data };
    });
  }, [nodes, edges, pulsedNodeIds, replayPulsed, deleteNodeById, flow?.phone_number]);

  // Color-coded, labelled edges with hover-dim opacity. The decorator
  // mirrors the source-handle color onto the wire + label chip so users
  // can read "Open hours → Menu" at a glance.
  const decoratedEdges = useMemo(
    () => decorateEdges(edges, nodes, {
      hoveredNodeId,
      selectedNodeId: selected?.id,
      trafficCounts: trafficEnabled ? (trafficData?.edges || {}) : null,
    }),
    [edges, nodes, hoveredNodeId, selected?.id, trafficEnabled, trafficData]
  );

  // Fetch traffic on toggle ON (or on flow change while still on)
  useEffect(() => {
    if (!trafficEnabled || !flowId || !subAccountId) return;
    let cancelled = false;
    setTrafficLoading(true);
    (async () => {
      try {
        const r = await api.get(`/api/sub-accounts/${subAccountId}/call-routing/${flowId}/traffic?days=7`);
        if (!cancelled) setTrafficData(r.data);
      } catch (_e) {
        if (!cancelled) setTrafficData({ edges: {}, total_calls: 0 });
      } finally {
        if (!cancelled) setTrafficLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [trafficEnabled, flowId, subAccountId, api]);

  const memoNodeTypes = useMemo(() => nodeTypes, []);

  return (
    <div
      data-vox-theme={theme}
      className="h-full flex flex-col overflow-hidden"
      style={{ background: 'var(--vox-canvas-bg)', color: 'var(--vox-text-primary)' }}
      data-testid="callflow-canvas-page"
    >
      {/* Top bar */}
      <header
        className="flex-shrink-0 h-14 px-4 flex items-center gap-3 border-b"
        style={{ background: 'var(--vox-chrome-bg)', borderColor: 'var(--vox-chrome-border)' }}
      >
        <button onClick={() => navigate(`/workspace/${flow?.workspace_id || ''}/sub-account/${subAccountId}/call-routing`)}
                className="p-1.5 rounded hover:bg-black/5 dark:hover:bg-white/5"
                style={{ color: 'var(--vox-text-secondary)' }}
                data-testid="canvas-back">
          <ArrowLeft size={16} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold truncate" style={{ color: 'var(--vox-text-primary)' }}>{flow?.name || 'Loading flow…'}</div>
          <div className="text-[10px]" style={{ color: 'var(--vox-text-muted)' }}>{flow?.phone_number}</div>
        </div>

        <button onClick={toggleEnabled} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold ${flow?.enabled ? 'bg-emerald-400/15 text-emerald-600 dark:text-emerald-300 border border-emerald-400/30' : 'bg-black/5 dark:bg-white/5 text-slate-500 dark:text-gray-400 border border-slate-200 dark:border-white/10'}`} data-testid="canvas-toggle-enabled">
          {flow?.enabled ? <ToggleRight size={13} /> : <ToggleLeft size={13} />} {flow?.enabled ? 'Live' : 'Disabled'}
        </button>
        <button onClick={tidy} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-black/5 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-700 dark:text-gray-300 text-[11px] hover:bg-black/10 dark:hover:bg-white/10" data-testid="canvas-tidy">
          <Sparkles size={13} /> Tidy
        </button>
        <button onClick={runSimulation} disabled={running} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-400/15 border border-emerald-400/40 text-emerald-600 dark:text-emerald-300 text-[11px] font-bold hover:bg-emerald-400/25 disabled:opacity-50" data-testid="canvas-run" title="Simulate the flow end-to-end — each node lights up as the call would travel">
          {running ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />} Test
        </button>
        <button onClick={() => setHistoryOpen(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-black/5 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-700 dark:text-gray-300 text-[11px] hover:bg-black/10 dark:hover:bg-white/10" data-testid="canvas-history">
          <History size={13} /> History
        </button>
        <button
          onClick={() => setTrafficEnabled((v) => !v)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold border ${
            trafficEnabled
              ? 'bg-sky-400/15 border-sky-400/40 text-sky-600 dark:text-sky-300'
              : 'bg-black/5 dark:bg-white/5 border-slate-200 dark:border-white/10 text-slate-700 dark:text-gray-300 hover:bg-black/10 dark:hover:bg-white/10'
          }`}
          title="Toggle live-traffic overlay (last 7 days)"
          data-testid="canvas-traffic-toggle"
        >
          {trafficLoading ? <Loader2 size={13} className="animate-spin" /> : <BarChart3 size={13} />}
          Traffic
          {trafficEnabled && trafficData && (
            <span className="ml-1 px-1.5 py-px rounded-full bg-sky-400/20 text-sky-200 text-[9px] font-bold" data-testid="canvas-traffic-total">
              {trafficData.total_calls || 0}
            </span>
          )}
        </button>
        <button
          onClick={verifyWebhook}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-400/15 border border-amber-400/40 text-amber-600 dark:text-amber-300 text-[11px] font-bold hover:bg-amber-400/25"
          title="Verify Twilio's actual webhook config for this number — and one-click repair if it's misconfigured"
          data-testid="canvas-verify-webhook"
        >
          <Wrench size={13} /> Verify & Repair
        </button>
        <button onClick={save} disabled={saving} className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-teal-400 text-[#08090d] text-[11px] font-bold hover:bg-teal-500 disabled:opacity-50" data-testid="canvas-save">
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
        </button>
      </header>

      {/* Body */}
      <div className="flex-1 flex overflow-hidden relative">
        <NodePalette />

        <div ref={flowRef} className="flex-1 relative" onDragOver={onDragOver} onDrop={onDrop} data-testid="callflow-canvas">
          <ReactFlow
            className="voxinity-flow"
            nodes={decoratedNodes}
            edges={decoratedEdges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            onNodeMouseEnter={(_, n) => setHoveredNodeId(n.id)}
            onNodeMouseLeave={() => setHoveredNodeId(null)}
            nodeTypes={memoNodeTypes}
            defaultEdgeOptions={{ type: 'smoothstep', animated: true, interactionWidth: 20 }}
            defaultViewport={flow?.canvas_viewport || { x: 80, y: 80, zoom: 0.85 }}
            fitView={!(flow?.canvas_viewport)}
            fitViewOptions={{ padding: 0.2 }}
            proOptions={{ hideAttribution: true }}
          >
            <Background color="var(--vox-grid)" gap={20} size={1} />
            <Controls position="bottom-left" />
            <MiniMap pannable zoomable nodeStrokeWidth={3} nodeColor={(n) => {
              const colors = { start: '#34d399', menu: '#38bdf8', time_router: '#38bdf8',
                tag_filter: '#38bdf8', contact_filter: '#38bdf8', ai_agent: '#2dd4bf',
                transfer: '#2dd4bf', hangup: '#fb7185' };
              return colors[n.type] || '#94a3b8';
            }} />
          </ReactFlow>

          <AnimatePresence>
            {selected && (
              <NodeConfigDrawer
                node={selected}
                agents={agents}
                phoneNumber={flow?.phone_number}
                phoneNumbers={phoneNumbers}
                currentFlowId={flow?.flow_id}
                onPhoneNumberChange={updateFlowPhoneNumber}
                onClose={() => setSelected(null)}
                onSave={updateNode}
                onDelete={deleteNode}
              />
            )}
          </AnimatePresence>

          <AnimatePresence>
            {historyOpen && (
              <ExecutionHistoryDrawer
                api={api}
                subAccountId={subAccountId}
                flowId={flowId}
                onClose={() => setHistoryOpen(false)}
                onReplay={replayExecution}
              />
            )}
          </AnimatePresence>

          <AnimatePresence>
            {verifyState && (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 12 }}
                className="absolute top-4 right-4 z-40 w-[440px] max-w-[92vw] rounded-2xl border shadow-2xl flex flex-col"
                style={{ background: 'var(--vox-chrome-bg)', borderColor: 'var(--vox-chrome-border)', maxHeight: 'calc(100vh - 120px)' }}
                data-testid="canvas-verify-panel"
              >
                <div className="flex items-center gap-2 px-4 py-3 border-b flex-shrink-0" style={{ borderColor: 'var(--vox-chrome-border)' }}>
                  <Wrench size={14} className="text-amber-500 dark:text-amber-300" />
                  <div className="text-xs font-bold uppercase tracking-wider flex-1" style={{ color: 'var(--vox-text-primary)' }}>Verify & Repair</div>
                  <button onClick={() => setVerifyState(null)} className="p-1 rounded hover:bg-black/5 dark:hover:bg-white/5" style={{ color: 'var(--vox-text-secondary)' }} data-testid="canvas-verify-close">
                    <X size={13} />
                  </button>
                </div>
                <div className="p-4 space-y-3 overflow-y-auto" data-testid="canvas-verify-scroll">
                  {verifyState.loading && (
                    <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--vox-text-secondary)' }}>
                      <Loader2 size={13} className="animate-spin" /> Checking Twilio…
                    </div>
                  )}
                  {verifyState.error && (
                    <div className="rounded-lg bg-rose-500/10 border border-rose-400/40 text-rose-600 dark:text-rose-300 text-xs px-3 py-2" data-testid="canvas-verify-error">
                      {verifyState.error}
                    </div>
                  )}
                  {verifyState.data && (
                    <>
                      {/* Top-of-panel actionable verdict — plain English summary of what's wrong */}
                      {verifyState.data.verdict && (
                        <div className={`rounded-xl px-3 py-3 text-xs border ${
                          verifyState.data.verdict.level === 'critical'
                            ? 'bg-rose-500/10 border-rose-400/50 text-rose-700 dark:text-rose-200'
                            : verifyState.data.verdict.level === 'warning'
                            ? 'bg-amber-400/10 border-amber-400/40 text-amber-700 dark:text-amber-200'
                            : 'bg-emerald-400/10 border-emerald-400/40 text-emerald-700 dark:text-emerald-200'
                        }`} data-testid="canvas-verify-verdict">
                          <div className="font-extrabold uppercase tracking-wider text-[11px] mb-1">
                            {verifyState.data.verdict.level === 'critical' ? '🔴' : verifyState.data.verdict.level === 'warning' ? '🟠' : '🟢'} {verifyState.data.verdict.title}
                          </div>
                          <div className="leading-relaxed">{verifyState.data.verdict.action}</div>
                        </div>
                      )}
                      <div className={`flex items-start gap-2 rounded-lg px-3 py-2.5 text-xs border ${
                        verifyState.data.matches
                          ? 'bg-emerald-400/10 border-emerald-400/40 text-emerald-700 dark:text-emerald-200'
                          : 'bg-amber-400/10 border-amber-400/40 text-amber-700 dark:text-amber-200'
                      }`} data-testid="canvas-verify-status">
                        {verifyState.data.matches
                          ? <CheckCircle2 size={14} className="mt-0.5 flex-shrink-0" />
                          : <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />}
                        <div className="flex-1">{verifyState.data.message}</div>
                      </div>
                      {verifyState.data.phone_number && (
                        <div className="space-y-1.5 text-[11px]">
                          <Row label="Phone" value={verifyState.data.phone_number} mono />
                          <Row label="Expected" value={verifyState.data.expected_url} mono break />
                          <Row label="Configured" value={verifyState.data.configured_url || '— not set —'} mono break warn={!verifyState.data.matches} />
                          {verifyState.data.voice_application_sid && (
                            <>
                              <div className="rounded-lg border border-rose-400/40 bg-rose-400/10 p-2 text-[10px] text-rose-700 dark:text-rose-200" data-testid="canvas-twiml-app-warning">
                                <span className="font-bold uppercase tracking-wider">⚠ TwiML App is overriding the webhook</span>
                                <div className="mt-1">Twilio routes inbound calls through this TwiML App instead of our voice_url. Clicking <span className="font-bold">Repair</span> will detach it.</div>
                              </div>
                              <Row label="TwiML App SID" value={verifyState.data.voice_application_sid} mono />
                              <Row label="TwiML App voiceUrl" value={verifyState.data.twiml_app_voice_url || '— unset —'} mono break warn />
                            </>
                          )}
                          {verifyState.data.trunk_sid && (
                            <>
                              <div className="rounded-lg border border-rose-400/40 bg-rose-400/10 p-2 text-[10px] text-rose-700 dark:text-rose-200">
                                <span className="font-bold uppercase tracking-wider">⚠ SIP trunk is bound to this number</span>
                                <div className="mt-1">All inbound calls route to the SIP trunk, bypassing our webhook entirely.</div>
                              </div>
                              <Row label="Trunk SID" value={verifyState.data.trunk_sid} mono />
                            </>
                          )}
                          {verifyState.data.voice_fallback_url && (
                            <Row label="Voice fallback URL" value={verifyState.data.voice_fallback_url} mono break warn />
                          )}
                          {verifyState.data.effective_url && verifyState.data.effective_url !== verifyState.data.configured_url && (
                            <Row label="Effective" value={verifyState.data.effective_url} mono break warn />
                          )}
                          {verifyState.data.voice_method && verifyState.data.voice_method !== 'POST' && (
                            <Row label="Method" value={`${verifyState.data.voice_method} (should be POST)`} warn />
                          )}
                        </div>
                      )}
                      {(verifyState.data.recent_inbound_calls || []).length > 0 && (
                        <div className="rounded-lg border p-2.5 space-y-1.5" style={{ borderColor: 'var(--vox-chrome-border)' }} data-testid="canvas-recent-inbound">
                          <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--vox-text-muted)' }}>
                            Twilio's view: last {verifyState.data.recent_inbound_calls.length} inbound calls
                          </div>
                          {verifyState.data.recent_inbound_calls.slice(0, 5).map((c, i) => {
                            const isFailed = ['busy', 'failed', 'no-answer', 'canceled'].includes(c.status);
                            return (
                              <div key={i} className="text-[10px] space-y-0.5" style={{ color: 'var(--vox-text-primary)' }}>
                                <div className="flex items-center gap-1.5">
                                  <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${
                                    isFailed ? 'bg-rose-500/20 text-rose-700 dark:text-rose-200' : 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-200'
                                  }`}>{c.status}</span>
                                  <span className="font-mono">{c.from_}</span>
                                  <span style={{ color: 'var(--vox-text-muted)' }}>·</span>
                                  <span>{c.duration || 0}s</span>
                                </div>
                                {(c.notifications || []).map((n, j) => (
                                  <div key={j} className="text-[9.5px] pl-3 text-rose-700 dark:text-rose-200">
                                    <span className="font-mono font-bold">Twilio {n.error_code}:</span>{' '}
                                    {n.message_text}
                                    {n.more_info && <a href={n.more_info} target="_blank" rel="noreferrer" className="underline ml-1 opacity-70">docs ↗</a>}
                                  </div>
                                ))}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {(verifyState.data.twilio_recent_errors || []).length > 0 && (
                        <div className="rounded-lg border border-rose-400/30 bg-rose-400/5 p-2.5 space-y-1.5" data-testid="canvas-twilio-errors">
                          <div className="text-[10px] font-bold uppercase tracking-wider text-rose-700 dark:text-rose-200">
                            Twilio errors (last 6h)
                          </div>
                          {verifyState.data.twilio_recent_errors.slice(0, 5).map((er, i) => (
                            <div key={i} className="text-[10px] text-rose-700 dark:text-rose-200">
                              <span className="font-mono font-bold">{er.error_code || '—'}</span>{' '}
                              <span>{er.message_text}</span>
                              {er.more_info && (
                                <a href={er.more_info} target="_blank" rel="noreferrer" className="underline ml-1 opacity-70">docs ↗</a>
                              )}
                            </div>
                          ))}
                          <div className="text-[9px] text-gray-500 dark:text-gray-400">
                            These are errors Twilio recorded itself. Common codes: 11200 = webhook unreachable, 13224 = invalid number, 32011 = geo-permission denied.
                          </div>
                        </div>
                      )}
                      {!verifyState.data.matches && (
                        <button
                          onClick={repairWebhook}
                          disabled={verifyState.loading}
                          className="w-full px-3 py-2 rounded-lg bg-teal-400 text-[#08090d] text-xs font-bold hover:bg-teal-300 disabled:opacity-50 flex items-center justify-center gap-1.5"
                          data-testid="canvas-verify-repair"
                        >
                          <Wrench size={13} /> Repair webhook now
                        </button>
                      )}
                      <button
                        onClick={diagnoseFlow}
                        disabled={verifyState.loading}
                        className="w-full px-3 py-2 rounded-lg bg-sky-400/15 border border-sky-400/40 text-sky-700 dark:text-sky-200 text-xs font-bold hover:bg-sky-400/25 disabled:opacity-50 flex items-center justify-center gap-1.5"
                        title="Simulate Twilio's inbound webhook and pull recent call logs"
                        data-testid="canvas-verify-diagnose"
                      >
                        🔬 Run deep diagnostic
                      </button>
                    </>
                  )}
                  {verifyState.diagnostic && (
                    <div className="space-y-3 pt-2 border-t" style={{ borderColor: 'var(--vox-chrome-border)' }} data-testid="canvas-diagnostic">
                      <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: 'var(--vox-text-muted)' }}>Diagnostic Report</div>
                      <ul className="space-y-1.5 text-[11px]" style={{ color: 'var(--vox-text-primary)' }}>
                        {(verifyState.diagnostic.advice || []).map((a, i) => (
                          <li key={i}>{a}</li>
                        ))}
                      </ul>
                      <div className="text-[10px]" style={{ color: 'var(--vox-text-muted)' }}>
                        Executions logged: <span className="font-mono font-bold" style={{ color: 'var(--vox-text-primary)' }}>{verifyState.diagnostic.executions_count}</span>
                      </div>
                      {/* Simulated chain — friendly per-hop labels */}
                      {(verifyState.diagnostic.simulated_chain || []).length > 0 && (() => {
                        const allOk = verifyState.diagnostic.simulated_chain.every(h => (h.status_code ?? 0) >= 200 && (h.status_code ?? 0) < 300);
                        const friendlyHopLabel = (h, i, total) => {
                          const url = h.url || '';
                          if (i === 0 || /\/route\//.test(url)) return 'Twilio entered the routing webhook';
                          const m = url.match(/\/node\/([^/?]+)/);
                          if (m) {
                            const nodeId = m[1];
                            if (/^n_start/.test(nodeId) || /start/.test(nodeId)) return 'Resolved the Start node';
                            if (/time/.test(nodeId)) return 'Evaluated Time Router';
                            if (/menu/.test(nodeId)) return 'Played the IVR menu prompt';
                            if (/tag/.test(nodeId)) return 'Filtered by contact tag';
                            if (/contact/.test(nodeId)) return 'Checked known vs unknown caller';
                            if (/transfer|xfer/.test(nodeId)) return 'Dialed the transfer number';
                            if (/closed/.test(nodeId)) return 'Took the closed-hours branch';
                            if (/open/.test(nodeId)) return 'Took the open-hours branch';
                            if (/ai|agent/.test(nodeId)) return 'Routed to AI Agent';
                            return `Evaluated node ${nodeId}`;
                          }
                          if (/\/voice\//.test(url)) return 'Handed off to the AI agent voice webhook';
                          return `Step ${i + 1}`;
                        };
                        return (
                          <div className={`rounded-xl border p-2.5 ${allOk ? 'bg-emerald-400/5 border-emerald-400/30' : 'bg-amber-400/5 border-amber-400/40'}`}>
                            <div className={`text-[10px] font-bold uppercase tracking-wider mb-2 ${allOk ? 'text-emerald-700 dark:text-emerald-300' : 'text-amber-700 dark:text-amber-300'}`}>
                              {allOk ? '✓ Simulated call chain — healthy' : '⚠ Simulated chain — see issues below'}
                            </div>
                            <ol className="space-y-1.5">
                              {verifyState.diagnostic.simulated_chain.map((h, i) => {
                                const ok = (h.status_code ?? 0) >= 200 && (h.status_code ?? 0) < 300;
                                return (
                                  <li key={i} className="rounded-md p-2 text-[10.5px] flex items-start gap-2" style={{ background: 'var(--vox-node-bg-soft)' }}>
                                    <span className={`flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold ${
                                      ok ? 'bg-emerald-500 text-white' : 'bg-rose-500 text-white'
                                    }`}>{ok ? '✓' : '!'}</span>
                                    <div className="flex-1 min-w-0">
                                      <div className="font-semibold" style={{ color: 'var(--vox-text-primary)' }}>
                                        Step {i + 1} — {friendlyHopLabel(h, i, verifyState.diagnostic.simulated_chain.length)}
                                      </div>
                                      {h.error && <div className="mt-0.5 text-rose-600 dark:text-rose-300">{h.error}</div>}
                                    </div>
                                  </li>
                                );
                              })}
                            </ol>
                            <details className="mt-2 text-[9.5px]">
                              <summary className="cursor-pointer select-none" style={{ color: 'var(--vox-text-muted)' }}>Show technical details (raw TwiML &amp; URLs)</summary>
                              <ol className="space-y-1.5 mt-2">
                                {verifyState.diagnostic.simulated_chain.map((h, i) => (
                                  <li key={i} className="rounded-md border p-2 font-mono break-all" style={{ borderColor: 'var(--vox-chrome-border)' }}>
                                    <div className="flex items-center gap-2">
                                      <span className={`px-1.5 py-0.5 rounded font-bold ${(h.status_code ?? 0) >= 200 && (h.status_code ?? 0) < 300 ? 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300' : 'bg-rose-500/20 text-rose-700 dark:text-rose-300'}`}>
                                        {h.status_code ?? h.status}
                                      </span>
                                      <span className="truncate flex-1" style={{ color: 'var(--vox-text-secondary)' }}>{h.url}</span>
                                    </div>
                                    {h.twiml && (
                                      <pre className="mt-1.5 whitespace-pre-wrap break-all text-[9px]" style={{ color: 'var(--vox-text-primary)' }}>{h.twiml}</pre>
                                    )}
                                  </li>
                                ))}
                              </ol>
                            </details>
                          </div>
                        );
                      })()}
                      {/* Twilio recent calls */}
                      {verifyState.diagnostic.twilio_recent_calls && (
                        <div>
                          <div className="text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color: 'var(--vox-text-secondary)' }}>Twilio recent calls</div>
                          {verifyState.diagnostic.twilio_recent_calls.length === 0 ? (
                            <div className="text-[10px]" style={{ color: 'var(--vox-text-muted)' }}>No recent calls — Twilio has never seen this number called.</div>
                          ) : (
                            <ul className="space-y-1">
                              {verifyState.diagnostic.twilio_recent_calls.map((c) => (
                                <li key={c.sid} className="rounded-md px-2 py-1.5 text-[10px] flex items-center gap-2" style={{ background: 'var(--vox-node-bg-soft)' }}>
                                  <span className={`px-1.5 py-0.5 rounded font-bold ${
                                    c.status === 'completed' ? 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300' :
                                    c.status === 'failed' ? 'bg-rose-500/20 text-rose-700 dark:text-rose-300' :
                                    'bg-amber-500/20 text-amber-700 dark:text-amber-300'
                                  }`}>{c.status}</span>
                                  <span className="font-mono" style={{ color: 'var(--vox-text-secondary)' }}>{c.from_}</span>
                                  <span style={{ color: 'var(--vox-text-muted)' }}>{c.duration ? `${c.duration}s` : '—'}</span>
                                  <span className="ml-auto" style={{ color: 'var(--vox-text-muted)' }}>{c.start_time?.slice(0, 16)?.replace('T', ' ')}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                          {verifyState.diagnostic.twilio_recent_calls_error && (
                            <div className="text-[10px] text-rose-600 dark:text-rose-300 mt-1">{verifyState.diagnostic.twilio_recent_calls_error}</div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {liveSession && (
              <LiveTestModal
                session={liveSession}
                wsStatus={wsStatus}
                executions={liveSteps}
                onClose={stopLiveTest}
                onStop={stopLiveTest}
              />
            )}
          </AnimatePresence>

          <AnimatePresence>
            {replay && (
              <ReplayPlayer
                execution={replay.exec}
                stepIndex={replay.stepIndex}
                playing={replay.playing}
                speed={replay.speed}
                onTogglePlay={replayTogglePlay}
                onStepBack={replayStepBack}
                onStepForward={replayStepForward}
                onSeek={replaySeek}
                onSpeedChange={replaySpeed}
                onClose={closeReplay}
              />
            )}
          </AnimatePresence>

          {/* Run result toast — small, transient */}
          <AnimatePresence>
            {runResult && (
              <motion.div
                initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
                className="absolute bottom-4 left-1/2 -translate-x-1/2 max-w-md w-[420px] bg-[#0c0d12] border border-teal-400/30 rounded-xl p-4 shadow-2xl"
                data-testid="run-result"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] uppercase tracking-wider text-teal-300 font-bold">Simulation result</span>
                  <button onClick={() => setRunResult(null)} className="text-gray-400 hover:text-white text-xs">×</button>
                </div>
                {runResult.error ? (
                  <p className="text-rose-300 text-sm">{runResult.error}</p>
                ) : (
                  <>
                    <div className="text-xs text-gray-300">Path: <span className="font-mono text-teal-300">{(runResult.path || []).join(' → ')}</span></div>
                    {runResult.leaf && (
                      <div className="mt-2 text-xs text-white">
                        Leaf action: <b className="text-teal-300">{runResult.leaf.type}</b>
                      </div>
                    )}
                  </>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

export default function CallFlowCanvasPage() {
  return (
    <SubAccountLayout>
      <ReactFlowProvider>
        <CanvasInner />
      </ReactFlowProvider>
    </SubAccountLayout>
  );
}


/* Small two-column row used inside the Verify & Repair panel. */
function Row({ label, value, mono = false, break: brk = false, warn = false }) {
  return (
    <div className="flex items-start gap-2">
      <span className="w-[72px] flex-shrink-0 text-[10px] uppercase tracking-wider" style={{ color: 'var(--vox-text-muted)' }}>{label}</span>
      <span
        className={`flex-1 ${mono ? 'font-mono' : ''} ${brk ? 'break-all' : 'truncate'} ${warn ? 'text-amber-600 dark:text-amber-300' : ''}`}
        style={!warn ? { color: 'var(--vox-text-primary)' } : undefined}
      >
        {value}
      </span>
    </div>
  );
}
